import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Upload, Image as ImageIcon, X, Check, Loader2, Trash2, Grid, List } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageLibrary = ({ open, onClose, onSelect, currentImage = null }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(currentImage);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch images from library
  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/images?limit=200`);
      const data = await res.json();
      setImages(data.images || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Error al cargar imágenes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchImages();
      setSelectedImage(currentImage);
    }
  }, [open, fetchImages, currentImage]);

  // Handle file upload
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const uploadedImages = [];
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await fetch(`${API_URL}/api/images/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          uploadedImages.push(data);
        } else {
          const error = await res.json();
          toast.error(`Error: ${error.detail || 'No se pudo subir'}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Error subiendo ${file.name}`);
      }
    }
    
    if (uploadedImages.length > 0) {
      toast.success(`${uploadedImages.length} imagen(es) subida(s)`);
      fetchImages();
      // Auto-select the first uploaded image
      if (uploadedImages[0]) {
        setSelectedImage(`${API_URL}${uploadedImages[0].url}`);
      }
    }
    
    setUploading(false);
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(Array.from(e.dataTransfer.files));
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(Array.from(e.target.files));
    }
  };

  // Handle image deletion
  const handleDelete = async (imageId, e) => {
    e.stopPropagation();
    if (!confirm('¿Eliminar esta imagen?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/images/${imageId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Imagen eliminada');
        fetchImages();
        if (selectedImage?.includes(imageId)) {
          setSelectedImage(null);
        }
      } else {
        toast.error('Error al eliminar');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Error al eliminar');
    }
  };

  // Handle selection confirm
  const handleConfirm = () => {
    if (selectedImage) {
      onSelect(selectedImage);
      onClose();
    }
  };

  // Get full URL for image
  const getFullUrl = (url) => {
    if (url?.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ImageIcon size={20} />
              Biblioteca de Imágenes
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="h-8 w-8 p-0"
              >
                <Grid size={16} />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-8 w-8 p-0"
              >
                <List size={16} />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
          
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <Loader2 size={20} className="animate-spin" />
              <span>Subiendo imágenes...</span>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload size={32} className="mx-auto text-gray-400" />
              <p className="text-sm text-gray-600">
                Arrastra imágenes aquí o{' '}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  selecciona archivos
                </button>
              </p>
              <p className="text-xs text-gray-400">
                JPG, PNG, GIF, WebP (máx. 10MB)
              </p>
            </div>
          )}
        </div>

        {/* Image Grid/List */}
        <div className="flex-1 overflow-y-auto mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-gray-400" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ImageIcon size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No hay imágenes en la biblioteca</p>
              <p className="text-sm">Sube tu primera imagen</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {images.map((img) => {
                const fullUrl = getFullUrl(img.url);
                const isSelected = selectedImage === fullUrl;
                
                return (
                  <div
                    key={img.filename}
                    onClick={() => setSelectedImage(fullUrl)}
                    className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer group border-2 transition-all ${
                      isSelected 
                        ? 'border-green-500 ring-2 ring-green-200' 
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={fullUrl}
                      alt={img.original_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                    
                    {/* Delete button */}
                    <button
                      onClick={(e) => handleDelete(img.id, e)}
                      className="absolute top-2 left-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={12} className="text-white" />
                    </button>
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                      <div className="p-2 text-white text-xs truncate w-full">
                        {img.original_name}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              {images.map((img) => {
                const fullUrl = getFullUrl(img.url);
                const isSelected = selectedImage === fullUrl;
                
                return (
                  <div
                    key={img.filename}
                    onClick={() => setSelectedImage(fullUrl)}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border transition-all ${
                      isSelected 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <img
                      src={fullUrl}
                      alt={img.original_name}
                      className="w-12 h-12 object-cover rounded"
                      loading="lazy"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{img.original_name}</p>
                      <p className="text-xs text-gray-500">{formatSize(img.size)}</p>
                    </div>
                    {isSelected && (
                      <Check size={18} className="text-green-500 flex-shrink-0" />
                    )}
                    <button
                      onClick={(e) => handleDelete(img.id, e)}
                      className="p-1 text-gray-400 hover:text-red-500 flex-shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t mt-4">
          <p className="text-sm text-gray-500">
            {images.length} imágenes • {selectedImage ? '1 seleccionada' : 'Ninguna seleccionada'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirm} 
              disabled={!selectedImage}
              className="bg-green-600 hover:bg-green-700"
            >
              <Check size={16} className="mr-1" />
              Usar imagen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageLibrary;
